// src/pages/SubjectPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Premium 24News Subject Page — with full i18n / translation support
//  Translation changes:
//   • Subject fetch includes ?lang=  → backend returns translated name/description
//   • Related fetch includes ?lang=  → translated names in sidebar
//   • translation_status badge shown when status !== 'done'
//   • Accept-Language header sent on every subject/related request
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";
import { Helmet } from "react-helmet-async";
// فوق الملف
import DOMPurify from "dompurify";

const sanitizeHtml = (html) =>
  DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: ["a", "b", "strong", "i", "em", "br", "p", "ul", "ol", "li"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
const autoDetectBase = (() => {
  const { protocol, hostname, port } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1")
    return `${protocol}//${hostname}:8000`;
  return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
})();
export const API_BASE = process.env.REACT_APP_API_BASE || autoDetectBase;

function slugify(text) {
  return (text || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

const isAbort = (err) =>
  err?.name === "AbortError" ||
  String(err?.message || "").toLowerCase().includes("aborted");

const b64UrlDecode = (str) => {
  try {
    str = str.replace(/-/g, "+").replace(/_/g, "/");
    while (str.length % 4) str += "=";
    return decodeURIComponent(
      atob(str).split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
    );
  } catch { return null; }
};

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

:root {
  --sp-red:      #e63311;
  --sp-red-dk:   #c4280d;
  --sp-ink:      #0d0d0d;
  --sp-ink-2:    #3d3d3d;
  --sp-ink-3:    #767676;
  --sp-bg:       #ffffff;
  --sp-bg-2:     #f8f8f7;
  --sp-bg-3:     #f0eeec;
  --sp-border:   rgba(0,0,0,0.08);
  --sp-shadow:   0 2px 16px rgba(0,0,0,0.07);
  --sp-shadow-h: 0 8px 32px rgba(0,0,0,0.13);
  --sp-radius:   14px;
  --sp-ease:     cubic-bezier(.4,0,.2,1);
  --sp-serif:    'Playfair Display', Georgia, serif;
  --sp-sans:     'DM Sans', -apple-system, sans-serif;
}

@media (prefers-color-scheme: dark) {
  :root {
    --sp-ink:    #f0ede8; --sp-ink-2: #c9c5be; --sp-ink-3: #888;
    --sp-bg:     #111110; --sp-bg-2:  #191917; --sp-bg-3:  #222220;
    --sp-border: rgba(255,255,255,0.07);
    --sp-shadow: 0 2px 16px rgba(0,0,0,0.4);
    --sp-shadow-h: 0 8px 32px rgba(0,0,0,0.55);
  }
}

@keyframes sp-up    { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
@keyframes sp-in    { from{opacity:0} to{opacity:1} }
@keyframes sp-shim  { from{background-position:-600px 0} to{background-position:600px 0} }
@keyframes sp-bar   { from{transform:scaleX(0)} to{transform:scaleX(1)} }
@keyframes sp-pop   { from{opacity:0;transform:scale(.9)} to{opacity:1;transform:scale(1)} }
@keyframes sp-slide { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:none} }
@keyframes sp-reply-in { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:none} }
@keyframes sp-like-pop { 0%{transform:scale(1)} 40%{transform:scale(1.35)} 70%{transform:scale(.88)} 100%{transform:scale(1)} }
@keyframes sp-spin  { to{transform:rotate(360deg)} }
@keyframes sp-pulse { 0%,100%{opacity:1} 50%{opacity:.5} }

@media (prefers-reduced-motion:reduce) { *,*::before,*::after{animation:none!important;transition:none!important} }

.sp-page { font-family:var(--sp-sans); background:var(--sp-bg); color:var(--sp-ink); min-height:100vh; }

/* RTL */
[dir="rtl"] .sp-hero__eyebrow-bar  { transform-origin:right; }
[dir="rtl"] .sp-hero__cover-img    { -webkit-mask-image:linear-gradient(to left,transparent 0%,rgba(0,0,0,.4) 60%,rgba(0,0,0,.8) 100%); mask-image:linear-gradient(to left,transparent 0%,rgba(0,0,0,.4) 60%,rgba(0,0,0,.8) 100%); }
[dir="rtl"] .sp-toc__item          { padding-left:18px; padding-right:18px; }
[dir="rtl"] .sp-toc__item::before  { left:auto; right:0; }
[dir="rtl"] .sp-toc__item:hover    { padding-left:18px; padding-right:24px; }
[dir="rtl"] .sp-toc__item--h3      { padding-left:18px; padding-right:32px; }
[dir="rtl"] .sp-toc__item--h3:hover{ padding-left:18px; padding-right:38px; }
[dir="rtl"] .sp-article__content h2{ padding-left:0; padding-right:14px; border-left:none; border-right:3px solid var(--sp-red); }
[dir="rtl"] .sp-comment            { flex-direction:row-reverse; }
[dir="rtl"] .sp-reply              { flex-direction:row-reverse; }
[dir="rtl"] .sp-replies            { margin-left:0; margin-right:52px; border-left:none; border-right:2px solid var(--sp-border); padding-left:0; padding-right:16px; }
[dir="rtl"] .sp-fab                { right:auto; left:16px; }

/* ── Translation badge ── */
.sp-tr-badge {
  display:inline-flex; align-items:center; gap:5px;
  font-size:.62rem; font-weight:700; letter-spacing:.06em; text-transform:uppercase;
  padding:3px 9px; border-radius:100px; vertical-align:middle;
}
.sp-tr-badge--pending   { background:rgba(234,179,8,.12);  color:#ca8a04; border:1px solid rgba(234,179,8,.25); }
.sp-tr-badge--in_progress { background:rgba(59,130,246,.1); color:#3b82f6; border:1px solid rgba(59,130,246,.2); }
.sp-tr-badge--failed    { background:rgba(239,68,68,.1);   color:#ef4444; border:1px solid rgba(239,68,68,.2); }
.sp-tr-badge--missing   { background:var(--sp-bg-3);       color:var(--sp-ink-3); border:1px solid var(--sp-border); }
.sp-tr-badge__spinner {
  width:8px; height:8px; border:1.5px solid currentColor; border-top-color:transparent;
  border-radius:50%; animation:sp-spin .7s linear infinite;
}
.sp-tr-badge--in_progress .sp-tr-badge__spinner { display:inline-block; }

/* ══════════════════════════════════════════════════════
   HERO
══════════════════════════════════════════════════════ */
.sp-hero {
  position:relative; background:var(--sp-ink); overflow:hidden;
  padding:clamp(40px,9vw,90px) clamp(16px,5vw,72px) clamp(32px,7vw,70px);
  animation:sp-in .4s var(--sp-ease) both;
}
.sp-hero__bg-grad {
  position:absolute; inset:0; z-index:0;
  background:radial-gradient(ellipse at 75% 40%,rgba(230,51,17,.2) 0%,transparent 60%),
             radial-gradient(ellipse at 15% 85%,rgba(230,51,17,.09) 0%,transparent 55%);
}
.sp-hero__noise {
  position:absolute; inset:0; z-index:0; pointer-events:none;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.03'/%3E%3C/svg%3E");
}
.sp-hero__cover-img {
  position:absolute; inset:0; z-index:0; width:100%; height:100%; object-fit:cover; opacity:.1;
  -webkit-mask-image:linear-gradient(to right,transparent 0%,rgba(0,0,0,.4) 60%,rgba(0,0,0,.8) 100%);
  mask-image:linear-gradient(to right,transparent 0%,rgba(0,0,0,.4) 60%,rgba(0,0,0,.8) 100%);
}
.sp-hero__inner {
  position:relative; z-index:1; max-width:1280px; margin:0 auto;
  display:grid; grid-template-columns:auto 1fr; gap:clamp(20px,4vw,48px); align-items:center;
}
@media (max-width:640px) { .sp-hero__inner { grid-template-columns:1fr; } }
.sp-hero__img-wrap { flex-shrink:0; animation:sp-pop .5s .1s var(--sp-ease) both; }
.sp-hero__img { width:clamp(100px,14vw,160px); height:clamp(100px,14vw,160px); border-radius:18px; object-fit:cover; box-shadow:0 12px 40px rgba(0,0,0,.35); display:block; }
.sp-hero__icon-box { width:clamp(100px,14vw,160px); height:clamp(100px,14vw,160px); border-radius:18px; background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.1); display:flex; align-items:center; justify-content:center; font-size:clamp(2.5rem,5vw,4rem); box-shadow:0 12px 40px rgba(0,0,0,.25); }
.sp-hero__content  { display:flex; flex-direction:column; gap:14px; }
.sp-hero__eyebrow  { display:inline-flex; align-items:center; gap:8px; font-size:.7rem; font-weight:700; letter-spacing:.13em; text-transform:uppercase; color:var(--sp-red); animation:sp-up .4s .1s var(--sp-ease) both; }
.sp-hero__eyebrow-bar { display:inline-block; width:24px; height:2px; background:var(--sp-red); border-radius:2px; transform-origin:left; animation:sp-bar .45s .15s var(--sp-ease) both; }
.sp-hero__title    { font-family:var(--sp-serif); font-size:clamp(2rem,5.5vw,4rem); font-weight:900; line-height:1.06; letter-spacing:-.02em; color:#fff; margin:0; animation:sp-up .45s .15s var(--sp-ease) both; text-shadow:0 4px 12px rgba(0,0,0,.4),0 8px 32px rgba(0,0,0,.6); }
.sp-hero__desc     { font-size:clamp(.9rem,1.8vw,1.05rem); font-weight:300; color:rgb(0, 0, 0); line-height:1.7; max-width:560px; margin:0; animation:sp-up .45s .2s var(--sp-ease) both; }
.sp-hero__meta-row { display:flex; align-items:center; gap:10px; flex-wrap:wrap; animation:sp-up .45s .22s var(--sp-ease) both; }
.sp-hero__stats    { display:flex; align-items:center; gap:20px; flex-wrap:wrap; animation:sp-up .45s .25s var(--sp-ease) both; }
.sp-hero__stat         { display:flex; align-items:baseline; gap:6px; }
.sp-hero__stat-num     { font-family:var(--sp-serif); font-size:1.6rem; font-weight:700; color:#fff; line-height:1; }
.sp-hero__stat-label   { font-size:.72rem; font-weight:500; letter-spacing:.04em; color:rgba(255,255,255,.45); }
.sp-hero__stat-divider { width:1px; height:28px; background:rgba(255,255,255,.12); }
.sp-hero__updated      { display:flex; align-items:center; gap:6px; font-size:.72rem; font-weight:500; color:rgba(255,255,255,.4); }

/* ══════════════════════════════════════════════════════
   ACTION BAR
══════════════════════════════════════════════════════ */
.sp-actions { position:relative; z-index:1; max-width:1280px; margin:0 auto; padding:0 clamp(16px,5vw,72px); transform:translateY(-1px); animation:sp-up .4s .3s var(--sp-ease) both; }
.sp-actions__bar { background:var(--sp-bg); border:1px solid var(--sp-border); border-radius:var(--sp-radius); box-shadow:var(--sp-shadow); padding:14px 20px; display:flex; align-items:center; gap:10px; flex-wrap:wrap; justify-content:space-between; }
.sp-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; font-size:.75rem; font-weight:700; letter-spacing:.04em; border-radius:100px; cursor:pointer; border:1.5px solid transparent; transition:all .18s var(--sp-ease); white-space:nowrap; font-family:var(--sp-sans); line-height:1; }
.sp-btn--ghost       { background:transparent; border-color:var(--sp-border); color:var(--sp-ink-2); }
.sp-btn--ghost:hover { border-color:var(--sp-ink-3); color:var(--sp-ink); background:var(--sp-bg-2); }
.sp-btn--like        { background:var(--sp-bg-2); border-color:var(--sp-border); color:var(--sp-ink-2); }
.sp-btn--like:hover  { border-color:#3b82f6; color:#3b82f6; background:rgba(59,130,246,.06); }
.sp-btn--like.active { background:rgba(59,130,246,.1); border-color:#3b82f6; color:#3b82f6; }
.sp-btn--save        { background:var(--sp-bg-2); border-color:var(--sp-border); color:var(--sp-ink-2); }
.sp-btn--save:hover  { border-color:#22c55e; color:#22c55e; background:rgba(34,197,94,.06); }
.sp-btn--save.active { background:rgba(34,197,94,.1); border-color:#22c55e; color:#22c55e; }
.sp-btn--dl          { background:var(--sp-bg-2); border-color:var(--sp-border); color:var(--sp-ink-2); }
.sp-btn--dl:hover    { background:var(--sp-bg-3); color:var(--sp-ink); }
.sp-btn--share       { background:var(--sp-red); border-color:var(--sp-red); color:#fff; }
.sp-btn--share:hover { background:var(--sp-red-dk); border-color:var(--sp-red-dk); transform:translateY(-1px); }
.sp-btn__count { background:rgba(0,0,0,.08); color:inherit; font-size:.65rem; font-weight:700; padding:2px 6px; border-radius:100px; min-width:22px; text-align:center; }
.sp-btn--like.active .sp-btn__count,
.sp-btn--save.active .sp-btn__count { background:rgba(0,0,0,.1); }
.sp-actions__dl-group { display:flex; gap:6px; }
.sp-actions__right    { display:flex; align-items:center; gap:8px; }

/* ══════════════════════════════════════════════════════
   BODY LAYOUT
══════════════════════════════════════════════════════ */
.sp-body { max-width:1280px; margin:0 auto; padding:clamp(24px,4vw,48px) clamp(16px,5vw,72px); display:grid; grid-template-columns:1fr 300px; gap:clamp(24px,4vw,56px); align-items:start; }
@media (max-width:1024px) { .sp-body { grid-template-columns:1fr; } }

/* ══════════════════════════════════════════════════════
   TOC
══════════════════════════════════════════════════════ */
.sp-toc { margin-bottom:32px; border:1px solid var(--sp-border); border-radius:var(--sp-radius); background:var(--sp-bg-2); overflow:hidden; animation:sp-up .4s .15s var(--sp-ease) both; }
.sp-toc__header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; cursor:pointer; user-select:none; border-bottom:1px solid transparent; transition:border-color .18s; }
.sp-toc__header--open { border-color:var(--sp-border); }
.sp-toc__header-left  { display:flex; align-items:center; gap:10px; }
.sp-toc__bar    { width:3px; height:16px; background:var(--sp-red); border-radius:2px; flex-shrink:0; }
.sp-toc__title  { font-size:.75rem; font-weight:700; letter-spacing:.09em; text-transform:uppercase; color:var(--sp-ink-3); margin:0; }
.sp-toc__toggle { width:26px; height:26px; border-radius:6px; border:1px solid var(--sp-border); background:var(--sp-bg); display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--sp-ink-3); transition:all .18s var(--sp-ease); }
.sp-toc__toggle:hover { background:var(--sp-bg-3); color:var(--sp-ink); }
.sp-toc__list   { padding:10px 0 8px; max-height:300px; overflow-y:auto; scrollbar-width:thin; }
.sp-toc__item   { display:block; width:100%; padding:7px 18px; text-align:left; background:none; border:none; font-size:.8rem; font-weight:500; color:var(--sp-ink-2); cursor:pointer; transition:all .15s var(--sp-ease); font-family:var(--sp-sans); position:relative; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sp-toc__item::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--sp-red); transform:scaleY(0); transform-origin:center; transition:transform .18s var(--sp-ease); }
.sp-toc__item:hover            { background:var(--sp-bg-3); color:var(--sp-red); padding-left:24px; }
.sp-toc__item:hover::before    { transform:scaleY(1); }
.sp-toc__item--h3              { padding-left:32px; font-size:.76rem; font-weight:400; }
.sp-toc__item--h3:hover        { padding-left:38px; }

/* ══════════════════════════════════════════════════════
   ARTICLE CARD
══════════════════════════════════════════════════════ */
.sp-article { background:var(--sp-bg); border:1px solid var(--sp-border); border-radius:var(--sp-radius); overflow:hidden; margin-bottom:28px; animation:sp-up .4s var(--sp-ease) both; transition:box-shadow .22s var(--sp-ease),border-color .22s var(--sp-ease); }
.sp-article:hover { box-shadow:var(--sp-shadow-h); border-color:rgba(230,51,17,.15); }
.sp-article__img-wrap { position:relative; aspect-ratio:16/7; overflow:hidden; background:var(--sp-bg-3); }
.sp-article__img      { width:100%; height:100%; object-fit:cover; display:block; transition:transform .4s var(--sp-ease); }
.sp-article:hover .sp-article__img { transform:scale(1.03); }
.sp-article__body   { padding:clamp(18px,3vw,28px); }
.sp-article__meta   { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:12px; }
.sp-article__author { display:flex; align-items:center; gap:8px; }
.sp-article__author-dot  { width:22px; height:22px; border-radius:50%; background:var(--sp-bg-3); display:flex; align-items:center; justify-content:center; font-size:.55rem; font-weight:800; color:var(--sp-ink-3); flex-shrink:0; }
.sp-article__author-name { font-size:.72rem; font-weight:600; color:var(--sp-ink-3); }
.sp-article__sep  { width:3px; height:3px; border-radius:50%; background:var(--sp-ink-3); opacity:.5; }
.sp-article__date { font-size:.7rem; font-weight:500; color:var(--sp-ink-3); }
.sp-article__title { font-family:var(--sp-serif); font-size:clamp(1.15rem,2.5vw,1.5rem); font-weight:700; line-height:1.25; margin:0 0 10px; color:var(--sp-ink); text-decoration:none; display:block; transition:color .18s; }
.sp-article__title:hover { color:var(--sp-red); }
.sp-article__content { font-size:.925rem; line-height:1.8; color:var(--sp-ink-2); }
.sp-article__content h2 { font-family:var(--sp-serif); font-size:clamp(1.1rem,2vw,1.35rem); font-weight:700; color:var(--sp-ink); margin:28px 0 12px; padding-left:14px; border-left:3px solid var(--sp-red); scroll-margin-top:120px; }
.sp-article__content h3 { font-size:clamp(.95rem,1.8vw,1.1rem); font-weight:700; color:var(--sp-ink); margin:20px 0 8px; scroll-margin-top:120px; }
.sp-article__content p  { margin-bottom:1rem; }
.sp-article__content a  { color:var(--sp-red); text-decoration:underline; text-decoration-color:rgba(230,51,17,.3); text-underline-offset:3px; }
.sp-article__content blockquote { border-left:3px solid var(--sp-red); margin:20px 0; padding:12px 20px; background:var(--sp-bg-2); border-radius:0 8px 8px 0; font-style:italic; }
.sp-article__read-more { display:inline-flex; align-items:center; gap:6px; margin-top:16px; font-size:.78rem; font-weight:700; letter-spacing:.05em; color:var(--sp-red); text-decoration:none; transition:gap .18s var(--sp-ease); }
.sp-article__read-more:hover { gap:10px; }
.sp-articles__empty      { padding:48px 24px; text-align:center; border:1.5px dashed var(--sp-border); border-radius:var(--sp-radius); background:var(--sp-bg-2); }
.sp-articles__empty-icon { font-size:2.5rem; margin-bottom:12px; opacity:.5; }
.sp-articles__empty-text { font-size:.875rem; color:var(--sp-ink-3); margin:0; }

/* ══════════════════════════════════════════════════════
   COMMENTS
══════════════════════════════════════════════════════ */
.sp-comments         { margin-top:40px; padding-top:36px; border-top:1px solid var(--sp-border); }
.sp-comments__heading { font-family:var(--sp-serif); font-size:clamp(1.2rem,2.5vw,1.5rem); font-weight:700; margin:0 0 24px; display:flex; align-items:center; gap:12px; }
.sp-comments__count  { font-size:.7rem; font-weight:700; letter-spacing:.07em; background:var(--sp-red); color:#fff; padding:3px 9px; border-radius:100px; font-family:var(--sp-sans); }
.sp-comment-form     { margin-bottom:32px; }
.sp-comment-form__inner { display:flex; gap:12px; align-items:flex-start; }
.sp-comment-form__avatar { width:40px; height:40px; border-radius:50%; background:linear-gradient(135deg,var(--sp-red),var(--sp-red-dk)); display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:800; color:#fff; flex-shrink:0; letter-spacing:.03em; }
.sp-comment-form__field  { flex:1; min-width:0; }
.sp-comment-form__textarea { width:100%; box-sizing:border-box; padding:12px 16px; font-size:.875rem; font-family:var(--sp-sans); color:var(--sp-ink); background:var(--sp-bg-2); border:1.5px solid var(--sp-border); border-radius:24px; resize:none; min-height:46px; max-height:200px; overflow-y:auto; outline:none; display:block; line-height:1.5; transition:border-color .18s var(--sp-ease),box-shadow .18s var(--sp-ease),border-radius .18s,min-height .18s; }
.sp-comment-form__textarea:focus { border-color:var(--sp-red); box-shadow:0 0 0 3px rgba(230,51,17,.1); background:var(--sp-bg); border-radius:14px; min-height:90px; }
.sp-comment-form__textarea::placeholder { color:var(--sp-ink-3); }
.sp-comment-form__actions { display:flex; align-items:center; justify-content:flex-end; gap:8px; margin-top:8px; animation:sp-in .2s var(--sp-ease) both; }
.sp-comment-form__submit { display:inline-flex; align-items:center; gap:7px; padding:8px 18px; font-size:.75rem; font-weight:700; letter-spacing:.05em; background:var(--sp-red); color:#fff; border:none; border-radius:100px; cursor:pointer; font-family:var(--sp-sans); transition:background .18s,transform .18s,opacity .18s; }
.sp-comment-form__submit:hover    { background:var(--sp-red-dk); transform:translateY(-1px); }
.sp-comment-form__submit:disabled { opacity:.5; cursor:not-allowed; transform:none; }
.sp-comment-form__cancel { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; font-size:.75rem; font-weight:600; background:transparent; color:var(--sp-ink-3); border:none; border-radius:100px; cursor:pointer; transition:color .15s; font-family:var(--sp-sans); }
.sp-comment-form__cancel:hover { color:var(--sp-ink); }
.sp-comment-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:0; }
.sp-comment { display:flex; gap:12px; padding:16px 0; border-bottom:1px solid var(--sp-border); animation:sp-slide .3s var(--sp-ease) both; }
.sp-comment:last-child { border-bottom:none; }
.sp-comment__avatar { width:40px; height:40px; border-radius:50%; flex-shrink:0; background:linear-gradient(135deg,var(--sp-bg-3),var(--sp-bg-2)); display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:800; color:var(--sp-ink-3); letter-spacing:.03em; border:1.5px solid var(--sp-border); }
.sp-comment__main   { flex:1; min-width:0; }
.sp-comment__bubble { background:var(--sp-bg-2); border-radius:0 16px 16px 16px; padding:12px 14px; display:inline-block; max-width:100%; word-break:break-word; transition:background .18s; }
.sp-comment__bubble:hover { background:var(--sp-bg-3); }
.sp-comment__username { font-size:.8rem; font-weight:700; color:var(--sp-ink); display:block; margin-bottom:4px; }
.sp-comment__text     { font-size:.875rem; line-height:1.6; color:var(--sp-ink-2); margin:0; }
.sp-comment__mention  { color:var(--sp-red); font-weight:700; margin-right:4px; }
.sp-comment__actions  { display:flex; align-items:center; gap:4px; margin-top:6px; flex-wrap:wrap; }
.sp-comment__action-btn { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; font-size:.72rem; font-weight:700; color:var(--sp-ink-3); background:none; border:none; border-radius:100px; cursor:pointer; transition:color .15s,background .15s; font-family:var(--sp-sans); line-height:1; }
.sp-comment__action-btn:hover                  { color:var(--sp-ink); background:var(--sp-bg-2); }
.sp-comment__action-btn--like:hover            { color:#ef4444; }
.sp-comment__action-btn--like.liked            { color:#ef4444; }
.sp-comment__action-btn--like.liked svg        { animation:sp-like-pop .4s var(--sp-ease) both; }
.sp-comment__action-btn--reply:hover           { color:var(--sp-red); }
.sp-comment__action-btn--delete:hover          { color:#ef4444; }
.sp-comment__time-dot   { color:var(--sp-ink-3); font-size:.62rem; padding:0 2px; }
.sp-comment__time-label { font-size:.68rem; font-weight:500; color:var(--sp-ink-3); }
.sp-replies { margin-top:4px; margin-left:52px; padding-left:16px; border-left:2px solid var(--sp-border); transition:border-color .18s; }
.sp-replies:hover { border-color:rgba(230,51,17,.2); }
.sp-replies__toggle { display:inline-flex; align-items:center; gap:7px; padding:6px 0; font-size:.75rem; font-weight:700; color:var(--sp-red); background:none; border:none; cursor:pointer; font-family:var(--sp-sans); transition:opacity .15s; }
.sp-replies__toggle:hover { opacity:.75; }
.sp-replies__toggle-icon      { display:inline-flex; transition:transform .2s var(--sp-ease); }
.sp-replies__toggle-icon--open { transform:rotate(180deg); }
.sp-reply-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:0; animation:sp-reply-in .25s var(--sp-ease) both; }
.sp-reply { display:flex; gap:10px; padding:12px 0; border-bottom:1px solid var(--sp-border); }
.sp-reply:last-child { border-bottom:none; }
.sp-reply__avatar { width:32px; height:32px; border-radius:50%; flex-shrink:0; background:linear-gradient(135deg,var(--sp-bg-3),var(--sp-bg-2)); display:flex; align-items:center; justify-content:center; font-size:.6rem; font-weight:800; color:var(--sp-ink-3); border:1.5px solid var(--sp-border); }
.sp-reply__main   { flex:1; min-width:0; }
.sp-reply__bubble { background:var(--sp-bg-2); border-radius:0 14px 14px 14px; padding:10px 12px; display:inline-block; max-width:100%; word-break:break-word; transition:background .18s; }
.sp-reply__bubble:hover { background:var(--sp-bg-3); }
.sp-reply__username { font-size:.76rem; font-weight:700; color:var(--sp-ink); display:block; margin-bottom:3px; }
.sp-reply__text     { font-size:.84rem; line-height:1.55; color:var(--sp-ink-2); margin:0; }
.sp-reply__mention  { color:var(--sp-red); font-weight:700; margin-right:4px; }
.sp-reply__actions  { display:flex; align-items:center; gap:4px; margin-top:5px; }
.sp-reply--pending  { opacity:.65; }
.sp-reply--pending .sp-reply__bubble { border:1px dashed var(--sp-border); }
.sp-reply-form { display:flex; gap:10px; align-items:flex-start; padding:10px 0 4px; animation:sp-reply-in .2s var(--sp-ease) both; }
.sp-reply-form__avatar { width:32px; height:32px; border-radius:50%; flex-shrink:0; background:linear-gradient(135deg,var(--sp-red),var(--sp-red-dk)); display:flex; align-items:center; justify-content:center; font-size:.6rem; font-weight:800; color:#fff; }
.sp-reply-form__field  { flex:1; min-width:0; }
.sp-reply-form__input  { width:100%; box-sizing:border-box; padding:9px 14px; font-size:.84rem; font-family:var(--sp-sans); color:var(--sp-ink); background:var(--sp-bg-2); border:1.5px solid rgba(230,51,17,.3); border-radius:100px; outline:none; display:block; resize:none; min-height:38px; max-height:120px; overflow-y:auto; line-height:1.4; transition:border-color .18s,box-shadow .18s,border-radius .18s; }
.sp-reply-form__input:focus { border-color:var(--sp-red); box-shadow:0 0 0 3px rgba(230,51,17,.1); background:var(--sp-bg); border-radius:12px; }
.sp-reply-form__input::placeholder { color:var(--sp-ink-3); }
.sp-reply-form__row  { display:flex; align-items:center; justify-content:flex-end; gap:6px; margin-top:6px; }
.sp-reply-form__send { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; border-radius:50%; background:var(--sp-red); color:#fff; border:none; cursor:pointer; transition:background .15s,transform .15s; }
.sp-reply-form__send:hover    { background:var(--sp-red-dk); transform:scale(1.1); }
.sp-reply-form__send:disabled { opacity:.4; cursor:not-allowed; transform:none; }
.sp-reply-form__cancel { display:inline-flex; align-items:center; gap:5px; padding:5px 12px; font-size:.72rem; font-weight:600; color:var(--sp-ink-3); background:none; border:none; border-radius:100px; cursor:pointer; font-family:var(--sp-sans); transition:color .15s; }
.sp-reply-form__cancel:hover { color:var(--sp-ink); }
.sp-replies__more   { display:inline-flex; align-items:center; gap:6px; padding:6px 0 10px; font-size:.72rem; font-weight:700; color:var(--sp-ink-3); background:none; border:none; cursor:pointer; font-family:var(--sp-sans); transition:color .15s; }
.sp-replies__more:hover { color:var(--sp-red); }
.sp-comments__empty { padding:32px 20px; text-align:center; border:1.5px dashed var(--sp-border); border-radius:12px; color:var(--sp-ink-3); font-size:.875rem; }

/* ══════════════════════════════════════════════════════
   SIDEBAR
══════════════════════════════════════════════════════ */
.sp-sidebar          { display:flex; flex-direction:column; gap:20px; position:sticky; top:80px; }
.sp-sidebar-card     { background:var(--sp-bg-2); border:1px solid var(--sp-border); border-radius:var(--sp-radius); overflow:hidden; animation:sp-up .4s .2s var(--sp-ease) both; }
.sp-sidebar-card__head  { padding:16px 18px 0; display:flex; align-items:center; gap:9px; }
.sp-sidebar-card__bar   { width:3px; height:16px; border-radius:2px; background:var(--sp-red); flex-shrink:0; }
.sp-sidebar-card__title { font-size:.72rem; font-weight:700; letter-spacing:.09em; text-transform:uppercase; color:var(--sp-ink-3); margin:0; }
.sp-sidebar-card__body  { padding:12px 18px 18px; }
.sp-related-list  { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:1px; }
.sp-related-item  { display:flex; align-items:center; gap:12px; padding:11px 0; border-bottom:1px solid var(--sp-border); text-decoration:none; color:inherit; transition:all .18s var(--sp-ease); }
.sp-related-item:last-child  { border-bottom:none; }
.sp-related-item:hover .sp-related-name { color:var(--sp-red); }
.sp-related-img   { width:52px; height:52px; border-radius:10px; object-fit:cover; flex-shrink:0; border:1px solid var(--sp-border); transition:transform .22s var(--sp-ease); }
.sp-related-item:hover .sp-related-img  { transform:scale(1.05); }
.sp-related-icon  { width:52px; height:52px; border-radius:10px; background:var(--sp-bg-3); display:flex; align-items:center; justify-content:center; font-size:1.5rem; flex-shrink:0; border:1px solid var(--sp-border); }
.sp-related-name  { font-size:.82rem; font-weight:700; color:var(--sp-ink); line-height:1.3; margin-bottom:2px; transition:color .18s; }
.sp-related-desc  { font-size:.72rem; color:var(--sp-ink-3); line-height:1.4; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.sp-stats-row            { display:flex; justify-content:space-between; align-items:center; padding:8px 0; border-bottom:1px solid var(--sp-border); }
.sp-stats-row:last-child { border-bottom:none; }
.sp-stats-row__label { font-size:.78rem; font-weight:500; color:var(--sp-ink-3); }
.sp-stats-row__value { font-family:var(--sp-serif); font-size:1.1rem; font-weight:700; color:var(--sp-ink); }

/* ══════════════════════════════════════════════════════
   MOBILE FAB
══════════════════════════════════════════════════════ */
.sp-fab { position:fixed; right:16px; bottom:20px; display:flex; flex-direction:column; gap:8px; z-index:1200; }
@media (min-width:768px) { .sp-fab { display:none; } }
.sp-fab__btn { width:44px; height:44px; border-radius:50%; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; box-shadow:0 4px 16px rgba(0,0,0,.18); transition:transform .18s var(--sp-ease),box-shadow .18s var(--sp-ease); }
.sp-fab__btn:hover           { transform:translateY(-2px) scale(1.06); box-shadow:0 8px 24px rgba(0,0,0,.24); }
.sp-fab__btn--like           { background:#fff; color:#3b82f6; }
.sp-fab__btn--like.active    { background:#3b82f6; color:#fff; }
.sp-fab__btn--save           { background:#fff; color:#22c55e; }
.sp-fab__btn--save.active    { background:#22c55e; color:#fff; }
.sp-fab__btn--share          { background:var(--sp-red); color:#fff; }

/* ══════════════════════════════════════════════════════
   AUTH MODAL
══════════════════════════════════════════════════════ */
.sp-modal-backdrop { position:fixed; inset:0; z-index:9000; background:rgba(0,0,0,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; padding:16px; animation:sp-in .2s var(--sp-ease) both; }
.sp-modal          { background:#1a1a18; border-radius:18px; width:100%; max-width:400px; box-shadow:0 24px 80px rgba(0,0,0,.7); overflow:hidden; animation:sp-pop .25s var(--sp-ease) both; }
.sp-modal__head    { padding:24px 24px 0; display:flex; align-items:flex-start; justify-content:space-between; }
.sp-modal__icon    { width:52px; height:52px; border-radius:14px; background:rgba(230,51,17,.15); border:1.5px solid rgba(230,51,17,.3); display:flex; align-items:center; justify-content:center; margin-bottom:16px; }
.sp-modal__close   { width:30px; height:30px; border-radius:8px; border:1px solid rgba(255,255,255,.1); background:rgba(255,255,255,.06); cursor:pointer; display:flex; align-items:center; justify-content:center; color:rgba(255,255,255,.55); font-size:.75rem; transition:all .15s; }
.sp-modal__close:hover { background:rgba(255,255,255,.1); }
.sp-modal__body    { padding:0 24px; }
.sp-modal__title   { font-family:var(--sp-sans); font-size:1.25rem; font-weight:700; color:#fff; margin:0 0 8px; }
.sp-modal__desc    { font-size:.875rem; color:rgba(255,255,255,.5); line-height:1.65; margin:0; }
.sp-modal__footer  { padding:20px 24px 24px; display:flex; gap:8px; justify-content:flex-end; }
.sp-modal__btn     { padding:11px 20px; font-size:.78rem; font-weight:700; letter-spacing:.04em; border-radius:100px; cursor:pointer; border:1.5px solid transparent; transition:all .18s var(--sp-ease); font-family:var(--sp-sans); }
.sp-modal__btn--ghost   { background:transparent; border-color:rgba(255,255,255,.15); color:rgba(255,255,255,.75); }
.sp-modal__btn--ghost:hover   { background:rgba(255,255,255,.06); }
.sp-modal__btn--outline { background:transparent; border-color:var(--sp-red); color:var(--sp-red); }
.sp-modal__btn--outline:hover { background:rgba(230,51,17,.08); }
.sp-modal__btn--primary { background:var(--sp-red); border-color:var(--sp-red); color:#fff; }
.sp-modal__btn--primary:hover { background:var(--sp-red-dk); }

/* ══════════════════════════════════════════════════════
   SKELETON
══════════════════════════════════════════════════════ */
.sp-skel      { background:linear-gradient(90deg,var(--sp-bg-2) 25%,var(--sp-bg-3) 50%,var(--sp-bg-2) 75%); background-size:600px 100%; animation:sp-shim 1.4s infinite linear; border-radius:6px; }
.sp-skel-hero { height:clamp(180px,25vw,300px); border-radius:0; background:var(--sp-bg-3); animation:sp-in .3s ease; }
.sp-skel-line { height:13px; margin-bottom:10px; }
.sp-skel-line--30  { width:30%; } .sp-skel-line--50 { width:50%; }
.sp-skel-line--70  { width:70%; } .sp-skel-line--100 { width:100%; }
.sp-skel-block     { border-radius:12px; }
.sp-skel-block--art  { height:180px; margin-bottom:20px; }
.sp-skel-block--side { height:240px; }
.sp-skel-body { max-width:1280px; margin:0 auto; padding:32px clamp(16px,5vw,72px); display:grid; grid-template-columns:1fr 280px; gap:48px; }
@media (max-width:1024px) { .sp-skel-body { grid-template-columns:1fr; } }
@media (max-width:480px) { .sp-replies { margin-left:40px; } .sp-comment__avatar { width:34px; height:34px; } .sp-comment-form__avatar { width:34px; height:34px; } }
`;

// ── Icons ─────────────────────────────────────────────────────────────────────
const Icon = {
  Like:     () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>,
  LikeFill: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3v11z"/></svg>,
  Save:     () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>,
  Share:    () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  Pdf:      () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>,
  Word:     () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  Clock:    () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Arrow:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  Delete:   () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>,
  X:        () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Lock:     () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e63311" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Send:     () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="2 21 23 12 2 3 2 10 17 12 2 14 2 21"/></svg>,
  Reply:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>,
  ChevD:    () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>,
  ChevU:    () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>,
  Book:     () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.5)" strokeWidth="1.5" strokeLinecap="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
};

// ── Translation status badge ───────────────────────────────────────────────────
// Shows when the backend is still generating a translation
const TR_LABELS = {
  pending:     { en:"Translating…", ar:"جاري الترجمة…", fr:"Traduction…",    de:"Wird übersetzt…" },
  in_progress: { en:"Translating…", ar:"جاري الترجمة…", fr:"Traduction…",    de:"Wird übersetzt…" },
  failed:      { en:"Translation failed", ar:"فشلت الترجمة", fr:"Échec traduction", de:"Übersetzung fehlgeschlagen" },
  missing:     { en:"Not translated yet", ar:"غير مترجم بعد", fr:"Non traduit",    de:"Noch nicht übersetzt" },
};

function TranslationBadge({ status, lang }) {
  if (!status || status === "done") return null;
  const labels = TR_LABELS[status] || TR_LABELS.missing;
  const label  = labels[lang] || labels.en;
  return (
    <span className={`sp-tr-badge sp-tr-badge--${status}`}>
      {(status === "pending" || status === "in_progress") && (
        <span className="sp-tr-badge__spinner" aria-hidden />
      )}
      {label}
    </span>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function inits(name = "") {
  return name.split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase() || "U";
}
function fmtDate(iso, lang = "en") {
  if (!iso) return "";
  try { return new Intl.DateTimeFormat(lang, { day:"numeric", month:"short", year:"numeric" }).format(new Date(iso)); }
  catch { return ""; }
}
function timeAgo(iso, lang = "en") {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return lang==="ar"?"الآن":lang==="fr"?"À l'instant":lang==="de"?"Gerade eben":"Just now";
    if (mins < 60) return lang==="ar"?`${mins} د`:lang==="fr"?`${mins} min`:lang==="de"?`${mins} Min.`:`${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return lang==="ar"?`${hrs} س`:lang==="fr"?`${hrs} h`:lang==="de"?`${hrs} Std.`:`${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7)  return lang==="ar"?`${days} ي`:lang==="fr"?`${days} j`:lang==="de"?`${days} T.`:`${days}d`;
    return fmtDate(iso, lang);
  } catch { return ""; }
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="sp-page">
      <style>{CSS}</style>
      <div className="sp-skel sp-skel-hero" />
      <div className="sp-skel-body">
        <div>{[1,2,3].map(i => <div key={i} className="sp-skel sp-skel-block sp-skel-block--art" style={{ animationDelay:`${i*.08}s` }} />)}</div>
        <div><div className="sp-skel sp-skel-block sp-skel-block--side" /></div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  REPLY FORM
// ══════════════════════════════════════════════════════════════════════════════
function ReplyForm({ parentId, replyToUsername, currentUserInits, onSubmit, onCancel, t }) {
  const [text, setText]       = useState("");
  const [sending, setSending] = useState(false);
  const inputRef              = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleSubmit(); }
    if (e.key === "Escape") onCancel();
  };

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try { await onSubmit(parentId, trimmed); setText(""); }
    finally { setSending(false); }
  };

  return (
    <div className="sp-reply-form">
      <div className="sp-reply-form__avatar">{currentUserInits}</div>
      <div className="sp-reply-form__field">
        <textarea
          ref={inputRef}
          className="sp-reply-form__input"
          placeholder={replyToUsername ? `${t("reply_to")} @${replyToUsername}…` : `${t("write_reply")}…`}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          aria-label={t("write_reply")}
        />
        {text.trim() && (
          <div className="sp-reply-form__row">
            <button className="sp-reply-form__cancel" onClick={onCancel} type="button">{t("cancel")}</button>
            <button className="sp-reply-form__send" onClick={handleSubmit} disabled={sending || !text.trim()} aria-label={t("post_reply")} type="button">
              <Icon.Send />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  SINGLE REPLY
// ══════════════════════════════════════════════════════════════════════════════
function ReplyItem({ reply, commentId, currentUserId, currentUsername, currentUserInits, onDeleteReply, onLikeReply, onReplyToReply, t, lang }) {
  const [showReplyForm, setShowReplyForm] = useState(false);

  const ownerId       = reply.user?.id ?? reply.user?.pk ?? null;
  const ownerUsername = reply.user?.username ?? null;
  const isOwner =
    (ownerId !== null && currentUserId !== null && String(ownerId) === String(currentUserId)) ||
    (ownerUsername && currentUsername && ownerUsername === currentUsername);

  const rawContent   = reply.content || "";
  const mentionMatch = rawContent.match(/^@(\S+)\s*(.*)/s);
  const mentionName  = mentionMatch ? mentionMatch[1] : null;
  const bodyText     = mentionMatch ? mentionMatch[2] : rawContent;

  return (
    <li className={`sp-reply${reply._pending ? " sp-reply--pending" : ""}`}>
      <div className="sp-reply__avatar">{inits(reply.user?.username || "U")}</div>
      <div className="sp-reply__main">
        <div className="sp-reply__bubble">
          <span className="sp-reply__username">{reply.user?.username ?? t("user_label")}</span>
          <p className="sp-reply__text">
            {mentionName && <span className="sp-reply__mention">@{mentionName}</span>}
            {bodyText}
          </p>
        </div>
        <div className="sp-reply__actions">
          <button
            className={`sp-comment__action-btn sp-comment__action-btn--like${reply._liked ? " liked" : ""}`}
            onClick={() => onLikeReply(commentId, reply.id)}
            aria-pressed={!!reply._liked}
            disabled={!!reply._pending}
          >
            {reply._liked ? <Icon.LikeFill /> : <Icon.Like />}
            {reply.likes_count > 0 && <span>{reply.likes_count}</span>}
          </button>
          <button className="sp-comment__action-btn sp-comment__action-btn--reply" onClick={() => setShowReplyForm(s => !s)} disabled={!!reply._pending}>
            <Icon.Reply /> {t("reply")}
          </button>
          <span className="sp-comment__time-dot">·</span>
          <span className="sp-comment__time-label" title={fmtDate(reply.created_at, lang)}>
            {reply._pending ? "…" : timeAgo(reply.created_at, lang)}
          </span>
          {isOwner && !reply._pending && (
            <>
              <span className="sp-comment__time-dot">·</span>
              <button className="sp-comment__action-btn sp-comment__action-btn--delete" onClick={() => onDeleteReply(commentId, reply.id)} aria-label={t("delete_comment")}>
                <Icon.Delete />
              </button>
            </>
          )}
        </div>
        {showReplyForm && (
          <ReplyForm
            parentId={commentId}
            replyToUsername={reply.user?.username}
            currentUserInits={currentUserInits}
            onSubmit={onReplyToReply}
            onCancel={() => setShowReplyForm(false)}
            t={t}
            lang={lang}
          />
        )}
      </div>
    </li>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  SINGLE COMMENT + REPLIES
// ══════════════════════════════════════════════════════════════════════════════
function CommentItem({ comment, index, currentUserId, currentUsername, currentUserInits, onDeleteComment, onLikeComment, onDeleteReply, onLikeReply, onSubmitReply, t, lang }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies,   setShowReplies]   = useState(false);
  const [visibleCount,  setVisibleCount]  = useState(3);

  const replies        = comment.replies || [];
  const replyCount     = replies.length;
  const visibleReplies = replies.slice(0, visibleCount);
  const hiddenCount    = replyCount - visibleCount;

  const ownerId       = comment.user?.id ?? comment.user?.pk ?? null;
  const ownerUsername = comment.user?.username ?? null;
  const isOwner =
    (ownerId !== null && currentUserId !== null && String(ownerId) === String(currentUserId)) ||
    (ownerUsername && currentUsername && ownerUsername === currentUsername);

  const handleReplySubmit = async (parentId, text) => {
    const fullText = `@${comment.user?.username || ""} ${text}`;
    await onSubmitReply(parentId, fullText);
    setShowReplies(true);
    setShowReplyForm(false);
  };

  return (
    <li className="sp-comment" style={{ animationDelay: `${index * 0.05}s` }}>
      <div className="sp-comment__avatar">{inits(comment.user?.username || "U")}</div>
      <div className="sp-comment__main">
        <div className="sp-comment__bubble">
          <span className="sp-comment__username">{comment.user?.username ?? t("user_label")}</span>
          <p className="sp-comment__text">{comment.content}</p>
        </div>
        <div className="sp-comment__actions">
          <button className={`sp-comment__action-btn sp-comment__action-btn--like${comment._liked ? " liked" : ""}`} onClick={() => onLikeComment(comment.id)} aria-pressed={!!comment._liked}>
            {comment._liked ? <Icon.LikeFill /> : <Icon.Like />}
            {comment.likes_count > 0 && <span>{comment.likes_count}</span>}
          </button>
          <button className="sp-comment__action-btn sp-comment__action-btn--reply" onClick={() => setShowReplyForm(s => !s)}>
            <Icon.Reply /> {t("reply")}
          </button>
          <span className="sp-comment__time-dot">·</span>
          <span className="sp-comment__time-label" title={fmtDate(comment.created_at, lang)}>
            {timeAgo(comment.created_at, lang)}
          </span>
          {isOwner && (
            <>
              <span className="sp-comment__time-dot">·</span>
              <button className="sp-comment__action-btn sp-comment__action-btn--delete" onClick={() => onDeleteComment(comment.id)} aria-label={t("delete_comment")}>
                <Icon.Delete />
              </button>
            </>
          )}
        </div>

        {showReplyForm && (
          <ReplyForm parentId={comment.id} replyToUsername={comment.user?.username} currentUserInits={currentUserInits} onSubmit={handleReplySubmit} onCancel={() => setShowReplyForm(false)} t={t} lang={lang} />
        )}

        {replyCount > 0 && (
          <div className="sp-replies">
            <button className="sp-replies__toggle" onClick={() => setShowReplies(s => !s)} aria-expanded={showReplies}>
              <span className={`sp-replies__toggle-icon${showReplies ? " sp-replies__toggle-icon--open" : ""}`}><Icon.ChevD /></span>
              {showReplies ? t("hide_replies") : t("view_replies", { count: replyCount })}
            </button>
            {showReplies && (
              <>
                <ul className="sp-reply-list">
                  {visibleReplies.map(r => (
                    <ReplyItem key={r.id} reply={r} commentId={comment.id} currentUserId={currentUserId} currentUsername={currentUsername} currentUserInits={currentUserInits} onDeleteReply={onDeleteReply} onLikeReply={onLikeReply} onReplyToReply={onSubmitReply} t={t} lang={lang} />
                  ))}
                </ul>
                {hiddenCount > 0 && (
                  <button className="sp-replies__more" onClick={() => setVisibleCount(c => c + 5)}>
                    <Icon.ChevD /> {t("load_more_replies", { count: hiddenCount })}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function SubjectPage() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const { t, lang } = useTranslation();

  const [subject,          setSubject]          = useState(null);
  const [translationStatus, setTranslationStatus] = useState(null); // ← NEW
  const [articles,         setArticles]         = useState([]);
  const [toc,              setToc]              = useState([]);
  const [related,          setRelated]          = useState([]);
  const [comments,         setComments]         = useState([]);
  const [isLiked,          setIsLiked]          = useState(false);
  const [isSaved,          setIsSaved]          = useState(false);
  const [likesCount,       setLikesCount]       = useState(0);
  const [savesCount,       setSavesCount]       = useState(0);
  const [loading,          setLoading]          = useState(true);
  const [commentText,      setCommentText]      = useState("");
  const [commentFocused,   setCommentFocused]   = useState(false);
  const [showAuthModal,    setShowAuthModal]    = useState(false);
  const [tocOpen,          setTocOpen]          = useState(true);
  const [shareToast,       setShareToast]       = useState(false);
  const [submitting,       setSubmitting]       = useState(false);

  const [currentUserId,   setCurrentUserId]   = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);
  const currentUserInits = currentUsername ? inits(currentUsername) : "Y";

  // ── Token ─────────────────────────────────────────────────────────────────
  const getToken = () => {
    try {
      return localStorage.getItem("accessToken") || localStorage.getItem("token") || localStorage.getItem("authToken") || null;
    } catch { return null; }
  };

  // JWT decode
  useEffect(() => {
    const tok = getToken();
    if (!tok) return;
    try {
      const parts = tok.split(".");
      if (parts.length >= 2) {
        const payload = b64UrlDecode(parts[1]);
        if (payload) {
          const obj = JSON.parse(payload);
          const id  = obj.user_id ?? obj.sub ?? obj.id ?? null;
          const un  = obj.username ?? obj.preferred_username ?? null;
          if (id && !currentUserId)   setCurrentUserId(id);
          if (un && !currentUsername) setCurrentUsername(un);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── apiFetch ──────────────────────────────────────────────────────────────
  const apiFetch = useCallback(async (url, opts = {}) => {
    const token   = getToken();
    const headers = new Headers(opts.headers || {});
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (token) {
      headers.set("Authorization", token.startsWith("Bearer ") ? token : `Bearer ${token}`);
      opts.credentials = opts.credentials || "omit";
    } else {
      opts.credentials = opts.credentials || "include";
    }
    const res = await fetch(url, { ...opts, headers });
    if (res.status === 401) {
      setShowAuthModal(true);
      const err = new Error("Unauthorized");
      err.response = res;
      throw err;
    }
    return res;
  }, []);

  // Silent /auth/me/
  const meFetch = async (signal) => {
    try {
      const token   = getToken();
      const headers = new Headers();
      headers.set("Accept", "application/json");
      if (token) {
        headers.set("Authorization", token.startsWith("Bearer ") ? token : `Bearer ${token}`);
        return await fetch(`${API_BASE}/auth/me/`, { headers, credentials:"omit", signal });
      }
      return await fetch(`${API_BASE}/auth/me/`, { headers, credentials:"include", signal });
    } catch { return null; }
  };

  useEffect(() => {
    const abort = new AbortController();
    (async () => {
      try {
        const res = await meFetch(abort.signal);
        if (res?.ok) {
          const data = await res.json();
          if (data?.id)       setCurrentUserId(data.id);
          if (data?.username) setCurrentUsername(data.username);
        }
      } catch {}
    })();
    return () => abort.abort();
  }, []);

  // ── Load subject + related + comments ─────────────────────────────────────
  // ★ KEY CHANGE: ?lang=${lang} added to subject & related fetches
  //   Backend now returns translated name/description + translation_status
  useEffect(() => {
    const abort = new AbortController();
    (async () => {
      setLoading(true);
      try {
        // ── Subject (with lang param + Accept-Language header) ────────────
        const res = await fetch(
          `${API_BASE}/subjects/${slug}/?lang=${lang}`,
          {
            signal:  abort.signal,
            headers: {
              "Accept":          "application/json",
              "Accept-Language": lang,
            },
          }
        );
        if (!res.ok) throw new Error("Subject not found");
        const data = await res.json();

        setSubject(data);
        setIsLiked(!!data.is_liked);
        setIsSaved(!!data.is_saved);
        setLikesCount(data.likes_count  || 0);
        setSavesCount(data.saves_count  || 0);
        // ★ Store translation_status so we can show badge
        setTranslationStatus(data.translation_status || null);

        await Promise.all([
          // ── Related subjects (also with lang param) ───────────────────
          (async () => {
            const r = await fetch(
              `${API_BASE}/subjects/${slug}/related/?lang=${lang}`,
              {
                signal:  abort.signal,
                headers: { "Accept-Language": lang },
              }
            );
            if (r.ok) {
              const raw = await r.json();
              setRelated(Array.isArray(raw) ? raw : raw.results || []);
            }
          })(),

          // ── Comments (no lang param needed — comments aren't translated) ──
          (async () => {
            const r = await fetch(
              `${API_BASE}/subjects/${slug}/comments/`,
              { signal: abort.signal }
            );
            if (r.ok) {
              const raw  = await r.json();
              const list = Array.isArray(raw) ? raw : raw.results || [];
              setComments(list.map(c => ({
                ...c,
                likes_count: Number(c.likes_count || 0),
                _liked:      !!c.is_liked,
                replies:     (c.replies || []).map(r2 => ({
                  ...r2,
                  likes_count: Number(r2.likes_count || 0),
                  _liked:      !!r2.is_liked,
                })),
              })));
            }
          })(),
        ]);
      } catch (e) {
        if (!isAbort(e)) console.error(e);
      } finally {
        if (!abort.signal.aborted) setLoading(false);
      }
    })();
    return () => abort.abort();
  }, [slug, lang]); // ★ re-fetch whenever lang changes

  // ── Load articles + TOC ───────────────────────────────────────────────────
  useEffect(() => {
    if (!subject) return;
    const abort = new AbortController();
    (async () => {
      try {
        const q   = (subject.search_query?.trim()) || subject.name;
        const url = `${API_BASE}/articles/?search=${encodeURIComponent(q)}&ordering=-created_at&page_size=20&lang=${lang}`;
        const res = await fetch(url, { signal: abort.signal });
        if (!res.ok) return setArticles([]);
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];

        const newToc    = [];
        const withAnchors = list.map((a) => {
          const wrapper = document.createElement("div");
          wrapper.innerHTML = a.content || "";
          wrapper.querySelectorAll("h2, h3").forEach((h) => {
            const text = h.textContent.trim();
            if (!text) return;
            const id = `a-${a.slug}-${slugify(text)}`;
            h.setAttribute("id", id);
            newToc.push({ id, text, tag: h.tagName.toLowerCase(), articleSlug: a.slug });
          });
          return { ...a, content_with_ids: wrapper.innerHTML };
        });
        setArticles(withAnchors);
        setToc(newToc);
      } catch (e) { if (!isAbort(e)) console.error(e); }
    })();
    return () => abort.abort();
  }, [subject, lang]);

  // ── Subject like / save ───────────────────────────────────────────────────
  const onLike = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/subjects/${slug}/like/`, { method:"POST", headers:{"Content-Type":"application/json"}, body:"{}" });
      if (res.ok) { const j = await res.json(); setIsLiked(j.liked); setLikesCount(j.likes_count); }
    } catch (e) { if (e.message !== "Unauthorized") console.error(e); }
  };

  const onSave = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/subjects/${slug}/save/`, { method:"POST", headers:{"Content-Type":"application/json"}, body:"{}" });
      if (res.ok) { const j = await res.json(); setIsSaved(j.saved); setSavesCount(j.saves_count); }
    } catch (e) { if (e.message !== "Unauthorized") console.error(e); }
  };

  const onDownload = async (fmt) => {
  try {
    const url = `${API_BASE}/subjects/${slug}/export/${fmt}/?lang=${lang}`;

    const res = await fetch(url, {
      headers: {
        Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
      },
    });

    if (!res.ok) throw new Error("Failed");

    const blob = await res.blob();

    const link = document.createElement("a");
    const objectUrl = window.URL.createObjectURL(blob);

    link.href = objectUrl;
    link.download = `${subject?.slug || "file"}-${lang}.${fmt}`;
    document.body.appendChild(link);
    link.click();

    window.URL.revokeObjectURL(objectUrl);
    document.body.removeChild(link);

  } catch (e) {
    console.error(e);
    alert(t("download_failed"));
  }
};

  const onShare = () => {
    const shareUrl = `${window.location.origin}/subject/${slug}`;
    if (navigator.share) {
      navigator.share({ title: subject?.name || "Subject", url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setShareToast(true);
        setTimeout(() => setShareToast(false), 2500);
      });
    }
  };

  // ── Submit top-level comment ──────────────────────────────────────────────
  const submitComment = async (e) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_BASE}/subjects/${slug}/comments/`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ content: text }),
      });
      if (res.ok) {
        const c = await res.json();
        setComments(prev => [...prev, { ...c, likes_count:Number(c.likes_count||0), _liked:!!c.is_liked, replies:[] }]);
        setCommentText(""); setCommentFocused(false);
      }
    } catch (e) { if (e.message !== "Unauthorized") console.error(e); }
    finally { setSubmitting(false); }
  };

  // ── Submit reply ──────────────────────────────────────────────────────────
  const submitReply = useCallback(async (commentId, text) => {
    const tempId = `temp-${Date.now()}`;
    setComments(prev => prev.map(c => {
      if (String(c.id) !== String(commentId)) return c;
      return { ...c, replies: [...(c.replies||[]), { id:tempId, content:text, created_at:new Date().toISOString(), likes_count:0, _liked:false, _pending:true, user:{ id:currentUserId, username:currentUsername||t("user_label") } }] };
    }));

    try {
      let res = null;
      try {
        res = await apiFetch(
          `${API_BASE}/subjects/${slug}/comments/${commentId}/replies/`,
          { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ content:text }) }
        );
        if (res.status === 404 || res.status === 405) res = null;
      } catch { res = null; }

      if (!res || !res.ok) {
        res = await apiFetch(
          `${API_BASE}/subjects/${slug}/comments/`,
          { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ content:text, parent_id:commentId }) }
        );
      }

      if (res && res.ok) {
        const confirmed = await res.json();
        setComments(prev => prev.map(c => {
          if (String(c.id) !== String(commentId)) return c;
          return { ...c, replies:(c.replies||[]).map(r => r.id === tempId ? { ...confirmed, likes_count:Number(confirmed.likes_count||0), _liked:!!confirmed.is_liked, _pending:false } : r) };
        }));
      }
    } catch (e) {
      if (e.message === "Unauthorized") {
        setComments(prev => prev.map(c => ({ ...c, replies:(c.replies||[]).filter(r => r.id !== tempId) })));
      }
    }
  }, [slug, apiFetch, currentUserId, currentUsername, t]);

  // ── Delete comment / reply ────────────────────────────────────────────────
  const deleteComment = async (commentId) => {
    if (!commentId) return;
    if (!window.confirm(t("confirm_delete_comment"))) return;
    try {
      const res = await apiFetch(`${API_BASE}/subjects/${slug}/comments/${commentId}/`, { method:"DELETE", headers:{"Content-Type":"application/json"} });
      if (res.ok || res.status === 204) setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
      else if (res.status === 403) alert(t("delete_forbidden"));
      else alert(`${t("delete_failed")} (${res.status}).`);
    } catch (e) { if (e.message !== "Unauthorized") alert(t("network_error")); }
  };

  const deleteReply = async (commentId, replyId) => {
    if (!replyId || String(replyId).startsWith("temp-")) return;
    if (!window.confirm(t("confirm_delete_comment"))) return;
    try {
      const res = await apiFetch(`${API_BASE}/subjects/${slug}/comments/${replyId}/`, { method:"DELETE", headers:{"Content-Type":"application/json"} });
      if (res.ok || res.status === 204) {
        setComments(prev => prev.map(c => String(c.id) === String(commentId) ? { ...c, replies:(c.replies||[]).filter(r => String(r.id) !== String(replyId)) } : c));
      } else if (res.status === 403) alert(t("delete_forbidden"));
      else alert(`${t("delete_failed")} (${res.status}).`);
    } catch (e) { if (e.message !== "Unauthorized") alert(t("network_error")); }
  };

  // ── Like comment / reply ──────────────────────────────────────────────────
  const likeComment = async (commentId) => {
    setComments(prev => prev.map(c => String(c.id)===String(commentId) ? { ...c, _liked:!c._liked, likes_count:Math.max(0,(c.likes_count||0)+(c._liked?-1:1)) } : c));
    try {
      const res = await apiFetch(`${API_BASE}/subjects/${slug}/comments/${commentId}/like/`, { method:"POST", headers:{"Content-Type":"application/json"}, body:"{}" });
      if (res.ok) { const j = await res.json(); setComments(prev => prev.map(c => String(c.id)===String(commentId) ? { ...c, _liked:!!j.liked, likes_count:Number(j.likes_count) } : c)); }
    } catch (e) {
      if (e.message === "Unauthorized") setComments(prev => prev.map(c => String(c.id)===String(commentId) ? { ...c, _liked:!c._liked, likes_count:Math.max(0,(c.likes_count||0)+(c._liked?-1:1)) } : c));
    }
  };

  const likeReply = async (commentId, replyId) => {
    if (String(replyId).startsWith("temp-")) return;
    setComments(prev => prev.map(c => String(c.id)===String(commentId) ? { ...c, replies:(c.replies||[]).map(r => String(r.id)===String(replyId) ? { ...r, _liked:!r._liked, likes_count:Math.max(0,(r.likes_count||0)+(r._liked?-1:1)) } : r) } : c));
    try {
      const res = await apiFetch(`${API_BASE}/subjects/${slug}/comments/${replyId}/like/`, { method:"POST", headers:{"Content-Type":"application/json"}, body:"{}" });
      if (res.ok) { const j = await res.json(); setComments(prev => prev.map(c => String(c.id)===String(commentId) ? { ...c, replies:(c.replies||[]).map(r => String(r.id)===String(replyId) ? { ...r, _liked:!!j.liked, likes_count:Number(j.likes_count) } : r) } : c)); }
    } catch (e) {
      if (e.message === "Unauthorized") setComments(prev => prev.map(c => String(c.id)===String(commentId) ? { ...c, replies:(c.replies||[]).map(r => String(r.id)===String(replyId) ? { ...r, _liked:!r._liked, likes_count:Math.max(0,(r.likes_count||0)+(r._liked?-1:1)) } : r) } : c));
    }
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior:"smooth", block:"start" });
  };

  const totalCommentCount = comments.reduce((acc, c) => acc + 1 + (c.replies?.filter(r => !r._pending).length || 0), 0);

  // ── Guards ────────────────────────────────────────────────────────────────
  if (loading && !subject) return <Skeleton />;
  if (!subject) return (
    <div className="sp-page">
      <style>{CSS}</style>
      <div style={{ maxWidth:480, margin:"80px auto", padding:"40px 32px", textAlign:"center", border:"1px solid var(--sp-border)", borderRadius:16, background:"var(--sp-bg-2)" }}>
        <p style={{ fontFamily:"var(--sp-serif)", fontSize:"4rem", fontWeight:900, color:"var(--sp-red)", margin:"0 0 12px" }}>404</p>
        <p style={{ color:"var(--sp-ink-2)", marginBottom:24 }}>{t("subject_not_found")}</p>
        <Link to="/" style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"11px 24px", borderRadius:100, background:"var(--sp-red)", color:"#fff", fontWeight:700, fontSize:".8rem", textDecoration:"none" }}>← {t("back_to_home")}</Link>
      </div>
    </div>
  );

  return (
    <div className="sp-page">
      <Helmet>
  <title>{subject?.name ? `${subject.name} | 24News` : "24News"}</title>

  <meta
    name="description"
    content={
      subject?.description ||
      "Read the latest articles, discussions, and updates on this topic."
    }
  />

  <meta property="og:type" content="article" />
  <meta property="og:title" content={subject?.name || "24News"} />
  <meta
    property="og:description"
    content={subject?.description || ""}
  />
  {subject?.image_url && (
    <meta property="og:image" content={subject.image_url} />
  )}

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={subject?.name || "24News"} />
  <meta
    name="twitter:description"
    content={subject?.description || ""}
  />
  {subject?.image_url && (
    <meta name="twitter:image" content={subject.image_url} />
  )}
  <link rel="canonical" href={`${window.location.origin}/${lang}/subject/${slug}`} />
</Helmet>
      <style>{CSS}</style>

      {/* ══════════════════════════ HERO ══════════════════════════════════ */}
      <section className="sp-hero">
        <div className="sp-hero__bg-grad" aria-hidden />
        <div className="sp-hero__noise"   aria-hidden />
        {subject.image_url && <img src={subject.image_url} alt="" className="sp-hero__cover-img" aria-hidden />}

        <div className="sp-hero__inner">
          <div className="sp-hero__img-wrap">
            {subject.image_url
              ? <img src={subject.image_url} alt={subject.name} className="sp-hero__img" />
              : <div className="sp-hero__icon-box">{subject.icon ? <i className={`bi ${subject.icon}`} style={{ fontSize:"2.5rem", color:"rgba(255,255,255,.7)" }} /> : <Icon.Book />}</div>
            }
          </div>

          <div className="sp-hero__content">
            <div className="sp-hero__eyebrow">
              <span className="sp-hero__eyebrow-bar" />
              24News · {t("subject_label")}
            </div>

            {/* ★ Title + translation badge side by side */}
            <h1 className="sp-hero__title">
              {subject.name}
            </h1>

            {/* ★ Translation status badge — shown when not yet "done" */}
            {translationStatus && translationStatus !== "done" && (
              <div className="sp-hero__meta-row">
                <TranslationBadge status={translationStatus} lang={lang} />
              </div>
            )}

{subject.description && (
  <div
    className="sp-hero__desc"
    dangerouslySetInnerHTML={{ __html: sanitizeHtml(subject.description) }}
  />
)}
            <div className="sp-hero__stats">
              {totalCommentCount > 0 && (
                <>
                  <div className="sp-hero__stat">
                    <span className="sp-hero__stat-num">{totalCommentCount}</span>
                    <span className="sp-hero__stat-label">{t("comments_label")}</span>
                  </div>
                  <div className="sp-hero__stat-divider" aria-hidden />
                </>
              )}
              <div className="sp-hero__stat">
                <span className="sp-hero__stat-num">{likesCount}</span>
                <span className="sp-hero__stat-label">{t("likes_label")}</span>
              </div>
              {savesCount > 0 && (
                <>
                  <div className="sp-hero__stat-divider" aria-hidden />
                  <div className="sp-hero__stat">
                    <span className="sp-hero__stat-num">{savesCount}</span>
                    <span className="sp-hero__stat-label">{t("saves_label")}</span>
                  </div>
                </>
              )}
              {subject.updated_at && (
                <>
                  <div className="sp-hero__stat-divider" aria-hidden />
                  <div className="sp-hero__updated"><Icon.Clock /> {t("updated_label")} {fmtDate(subject.updated_at, lang)}</div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ ACTION BAR ════════════════════════════ */}
      <div className="sp-actions">
        <div className="sp-actions__bar">
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <button className={`sp-btn sp-btn--like${isLiked?" active":""}`} onClick={onLike} aria-pressed={isLiked}>
              <Icon.Like /> {isLiked ? t("liked") : t("like")}
              <span className="sp-btn__count">{likesCount}</span>
            </button>
            <button className={`sp-btn sp-btn--save${isSaved?" active":""}`} onClick={onSave} aria-pressed={isSaved}>
              <Icon.Save /> {isSaved ? t("saved") : t("save")}
              <span className="sp-btn__count">{savesCount}</span>
            </button>
          </div>
          <div className="sp-actions__right">
            <div className="sp-actions__dl-group">
              <button className="sp-btn sp-btn--dl" onClick={() => onDownload("pdf")}><Icon.Pdf /> {t("download_pdf")}</button>
              <button className="sp-btn sp-btn--dl" onClick={() => onDownload("docx")}><Icon.Word /> {t("download_word")}</button>
            </div>
            <button className="sp-btn sp-btn--share" onClick={onShare}>
              <Icon.Share /> {shareToast ? t("link_copied") : t("share")}
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════ BODY ══════════════════════════════════ */}
      <div className="sp-body">
        <main>
          {/* TOC */}
          {toc.length > 0 && (
            <div className="sp-toc">
              <div className={`sp-toc__header${tocOpen?" sp-toc__header--open":""}`} onClick={() => setTocOpen(s => !s)} role="button" aria-expanded={tocOpen} tabIndex={0} onKeyDown={e => e.key==="Enter" && setTocOpen(s => !s)}>
                <div className="sp-toc__header-left"><span className="sp-toc__bar" /><h2 className="sp-toc__title">{t("table_of_contents")}</h2></div>
                <div className="sp-toc__toggle" aria-hidden>{tocOpen ? <Icon.ChevU /> : <Icon.ChevD />}</div>
              </div>
              {tocOpen && (
                <div className="sp-toc__list">
                  {toc.map(h => (
                    <button key={h.id} className={`sp-toc__item${h.tag==="h3"?" sp-toc__item--h3":""}`} onClick={() => scrollToId(h.id)}>
                      {h.tag==="h3"?"↳ ":""}{h.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════ COMMENTS ══════════════════════════════ */}
          <section className="sp-comments">
            <h2 className="sp-comments__heading">
              {t("comments_heading")}
              {totalCommentCount > 0 && <span className="sp-comments__count">{totalCommentCount}</span>}
            </h2>

            <form className="sp-comment-form" onSubmit={submitComment}>
              <div className="sp-comment-form__inner">
                <div className="sp-comment-form__avatar">{currentUserInits}</div>
                <div className="sp-comment-form__field">
                  <textarea
                    className="sp-comment-form__textarea"
                    placeholder={t("comment_placeholder")}
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onFocus={() => setCommentFocused(true)}
                    rows={1}
                    required
                    aria-label={t("comment_placeholder")}
                  />
                  {(commentFocused || commentText) && (
                    <div className="sp-comment-form__actions">
                      <button type="button" className="sp-comment-form__cancel" onClick={() => { setCommentText(""); setCommentFocused(false); }}>{t("cancel")}</button>
                      <button className="sp-comment-form__submit" type="submit" disabled={submitting || !commentText.trim()}>
                        <Icon.Send /> {t("post_comment")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </form>

            {comments.length === 0
              ? <div className="sp-comments__empty">{t("no_comments_yet")}</div>
              : (
                <ul className="sp-comment-list">
                  {comments.map((c, i) => (
                    <CommentItem
                      key={c.id} comment={c} index={i}
                      currentUserId={currentUserId} currentUsername={currentUsername} currentUserInits={currentUserInits}
                      onDeleteComment={deleteComment} onLikeComment={likeComment}
                      onDeleteReply={deleteReply}     onLikeReply={likeReply}
                      onSubmitReply={submitReply}
                      t={t} lang={lang}
                    />
                  ))}
                </ul>
              )
            }
          </section>
        </main>

        {/* ══════════════════════════ SIDEBAR ═══════════════════════════ */}
        <aside className="sp-sidebar">
          {related.length > 0 && (
            <div className="sp-sidebar-card">
              <div className="sp-sidebar-card__head"><span className="sp-sidebar-card__bar" /><h3 className="sp-sidebar-card__title">{t("related_subjects")}</h3></div>
              <div className="sp-sidebar-card__body">
                <ul className="sp-related-list">
                  {related.map(r => (
                    <li key={r.id}>
                      <Link className="sp-related-item" to={`/subject/${r.slug}`}>
                        {r.image_url
                          ? <img src={r.image_url} alt={r.name} className="sp-related-img" />
                          : <div className="sp-related-icon">{r.icon ? <i className={`bi ${r.icon}`} style={{ color:"var(--sp-red)" }} /> : "📚"}</div>
                        }
                        <div>
                          <div className="sp-related-name">{r.name}</div>
                          {r.description && <div className="sp-related-desc">{r.description}</div>}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="sp-sidebar-card">
            <div className="sp-sidebar-card__head"><span className="sp-sidebar-card__bar" /><h3 className="sp-sidebar-card__title">{t("about_subject")}</h3></div>
            <div className="sp-sidebar-card__body">
              {[
                { label: t("comments_label"), value: totalCommentCount },
                { label: t("likes_label"),    value: likesCount },
                { label: t("saves_label"),    value: savesCount },
              ].map(({ label, value }) => (
                <div key={label} className="sp-stats-row">
                  <span className="sp-stats-row__label">{label}</span>
                  <span className="sp-stats-row__value">{value}</span>
                </div>
              ))}
              {/* ★ Translation status in sidebar too */}
              {translationStatus && translationStatus !== "done" && (
                <div className="sp-stats-row">
                  <span className="sp-stats-row__label">{t("translation_label") || "Translation"}</span>
                  <TranslationBadge status={translationStatus} lang={lang} />
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* MOBILE FAB */}
      <div className="sp-fab">
        <button className="sp-fab__btn sp-fab__btn--share" onClick={onShare} aria-label={t("share")}><Icon.Share /></button>
        <button className={`sp-fab__btn sp-fab__btn--save${isSaved?" active":""}`} onClick={onSave} aria-label={t("save")}><Icon.Save /></button>
        <button className={`sp-fab__btn sp-fab__btn--like${isLiked?" active":""}`} onClick={onLike} aria-label={t("like")}><Icon.Like /></button>
      </div>

      {/* AUTH MODAL */}
      {showAuthModal && (
        <div className="sp-modal-backdrop" onClick={e => e.target===e.currentTarget && setShowAuthModal(false)}>
          <div className="sp-modal" role="dialog" aria-modal="true">
            <div className="sp-modal__head">
              <div className="sp-modal__icon"><Icon.Lock /></div>
              <button className="sp-modal__close" onClick={() => setShowAuthModal(false)} aria-label={t("close")}><Icon.X /></button>
            </div>
            <div className="sp-modal__body">
              <h2 className="sp-modal__title">{t("sign_in_required")}</h2>
              <p className="sp-modal__desc">{t("sign_in_desc")}</p>
            </div>
            <div className="sp-modal__footer">
              <button className="sp-modal__btn sp-modal__btn--ghost"   onClick={() => setShowAuthModal(false)}>{t("cancel")}</button>
              <button className="sp-modal__btn sp-modal__btn--outline" onClick={() => { setShowAuthModal(false); navigate("/register"); }}>{t("create_account")}</button>
              <button className="sp-modal__btn sp-modal__btn--primary" onClick={() => { setShowAuthModal(false); navigate("/login"); }}>{t("login")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
